import api from "config/axiosConfig";
import { apiRoutes } from "util/apiRoutes";

export interface PlacementRelationRow {
  id: string;
  node_id: number;
  node_type: string;
  related_id: number;
  related_type: string;
  rel_type: string;
  properties: {
    placement_type?: string;
    layout?: { x: number; y: number; scale: number; rotation: number };
    text_overlay?: string;
    poll_comment_id?: number | null;
  };
  value_int: number | null;
  is_active: boolean;
}

export interface ListPlacementsParams {
  relatedType: "user" | "team" | "issue";
  relatedId: number;
  placementType?: "story" | "profile" | "resteep";
  limit?: number;
  offset?: number;
}

/**
 * Fetch placement relations for a profile or story surface.
 */
export async function listPlacements(
  params: ListPlacementsParams
): Promise<PlacementRelationRow[]> {
  const res = await api.get<{ items: PlacementRelationRow[] }>(apiRoutes.entities.placements, {
    params: {
      related_type: params.relatedType,
      related_id: params.relatedId,
      placement_type: params.placementType,
      limit: params.limit,
      offset: params.offset,
    },
  });
  return Array.isArray(res.data?.items) ? res.data.items : [];
}

export interface SavePlacementBody {
  node_type: string;
  node_id: number;
  related_type: "user" | "team";
  related_id: number;
  properties: {
    placement_type: "story" | "profile";
    layout?: { x: number; y: number; scale: number; rotation: number };
    text_overlay?: string;
    poll_comment_id?: number | null;
  };
  sort_order?: number | null;
}

/**
 * Create or update a placement on the caller's surface.
 */
export async function savePlacement(body: SavePlacementBody): Promise<PlacementRelationRow> {
  const res = await api.post<PlacementRelationRow>(apiRoutes.entities.placements, {
    node_type: body.node_type,
    node_id: body.node_id,
    related_type: body.related_type,
    related_id: body.related_id,
    properties: body.properties,
    sort_order: body.sort_order,
  });
  return res.data;
}

/**
 * Remove a placement relation.
 */
export async function deletePlacement(relationId: string): Promise<void> {
  await api.delete(apiRoutes.entities.placementById(relationId));
}

// ---------------------------------------------------------------------------
// Resteep (placement-based crosspost)
// ---------------------------------------------------------------------------

export type ResteepSourceEntityType =
  | "impact"
  | "teabate"
  | "solution"
  | "action"
  | "comment"
  | "issue"
  | "feedback";

export interface CreateResteepBody {
  source_entity_type: ResteepSourceEntityType;
  source_entity_id: number | string;
  target_entity_type: "issue" | "user";
  target_entity_id: number | string;
  content?: string;
  properties?: {
    privacy?: "public" | "private" | "anonymous";
  };
  is_anonymous?: boolean;
}

export interface CreateResteepResponse {
  comment: {
    id: number;
    commentable_type: string;
    commentable_id: number;
    user_id: number;
    kind: string;
    content: string;
    properties: Record<string, unknown>;
    created_at: string;
  };
  placement: PlacementRelationRow;
}

/**
 * Create a resteep (placement-based).
 * Returns both the placement entity_rel and the associated comment.
 */
export async function createResteep(body: CreateResteepBody): Promise<CreateResteepResponse> {
  const res = await api.post<CreateResteepResponse>(apiRoutes.comments.resteep, body);
  return res.data;
}
